import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Calculator, Plus, Edit, Trash2, Search, FileText, CheckCircle, Clock, Download } from 'lucide-react';
import { lossCalAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import * as XLSX from 'xlsx';

const LossCal = () => {
  const { canAdd, canEdit, canDelete } = useAuth();
  const [lossRecords, setLossRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const [newRecord, setNewRecord] = useState({
    date: '',
    dept: '',
    wt: '',
    actPurity: '',
    less: '',  // Changed from pureLess
    pureBeforeWt: '',
    afterWt: '',
    pureLoss: '',  // Now manual entry
    remarks: '',
    status: 'On Process'
  });

  // Load loss calculation records
  const loadLossRecords = async () => {
    try {
      setIsLoading(true);
      const records = await lossCalAPI.getAll();
      setLossRecords(records);
    } catch (error) {
      toast.error('Failed to load loss calculation records');
      console.error('Error loading loss records:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLossRecords();
  }, []);

  // Calculate auto-generated fields based on EXACT Excel formulas
  const calculateFields = (record) => {
    const wt = parseFloat(record.wt) || 0;
    const actPurity = parseFloat(record.actPurity) || 0;
    const less = parseFloat(record.less) || 0;
    const pureLoss = parseFloat(record.pureLoss) || 0; // Now manual entry

    // PURE (Column E) = WT × ACT PURITY%
    const pure = wt * (actPurity / 100);

    // REC (Column G) = WT × (ACT PURITY - LESS)%
    const rec = wt * ((actPurity - less) / 100);

    // BALANCE PURE = PURE - PURE LOSS (E - J)
    const balancePure = pure - pureLoss;

    return {
      pure: pure.toFixed(3),
      rec: rec.toFixed(3),
      balancePure: balancePure.toFixed(3)
    };
  };

  // Add new loss record
  const handleAddRecord = async () => {
    if (!newRecord.date || !newRecord.dept || !newRecord.wt || !newRecord.actPurity || !newRecord.pureLoss) {
      toast.error('Please fill all required fields (Date, Dept, WT, Act Purity, Pure Loss)');
      return;
    }

    try {
      const calculatedFields = calculateFields(newRecord);
      const recordToAdd = {
        ...newRecord,
        wt: parseFloat(newRecord.wt),
        actPurity: parseFloat(newRecord.actPurity),
        less: parseFloat(newRecord.less) || 0,
        pureBeforeWt: parseFloat(newRecord.pureBeforeWt) || 0,
        afterWt: parseFloat(newRecord.afterWt) || 0,
        pureLoss: parseFloat(newRecord.pureLoss),  // Now manual entry
        ...calculatedFields
      };

      const created = await lossCalAPI.create(recordToAdd);
      setLossRecords([...lossRecords, created]);
      setIsAddDialogOpen(false);
      setNewRecord({
        date: '',
        dept: '',
        wt: '',
        actPurity: '',
        less: '',
        pureBeforeWt: '',
        afterWt: '',
        pureLoss: '',
        remarks: '',
        status: 'On Process'
      });
      toast.success('Loss calculation record added successfully');
    } catch (error) {
      toast.error('Failed to add loss record');
      console.error('Error adding loss record:', error);
    }
  };

  // Edit existing record
  const openEditDialog = (record) => {
    setEditingRecord({ ...record });
    setIsEditDialogOpen(true);
  };

  const handleEditRecord = async () => {
    if (!editingRecord.date || !editingRecord.dept || !editingRecord.wt || !editingRecord.actPurity || !editingRecord.pureLoss) {
      toast.error('Please fill all required fields (Date, Dept, WT, Act Purity, Pure Loss)');
      return;
    }

    try {
      const calculatedFields = calculateFields(editingRecord);
      const recordToUpdate = {
        ...editingRecord,
        wt: parseFloat(editingRecord.wt),
        actPurity: parseFloat(editingRecord.actPurity),
        less: parseFloat(editingRecord.less) || 0,
        pureBeforeWt: parseFloat(editingRecord.pureBeforeWt) || 0,
        afterWt: parseFloat(editingRecord.afterWt) || 0,
        pureLoss: parseFloat(editingRecord.pureLoss),  // Manual entry
        ...calculatedFields
      };

      const updated = await lossCalAPI.update(editingRecord.id, recordToUpdate);
      setLossRecords(lossRecords.map(r => r.id === editingRecord.id ? updated : r));
      setIsEditDialogOpen(false);
      setEditingRecord(null);
      toast.success('Loss calculation record updated successfully');
    } catch (error) {
      toast.error('Failed to update loss record');
      console.error('Error updating loss record:', error);
    }
  };

  // Delete record
  const handleDeleteRecord = async (id) => {
    if (!confirm('Are you sure you want to delete this loss calculation record?')) {
      return;
    }

    try {
      await lossCalAPI.delete(id);
      setLossRecords(lossRecords.filter(r => r.id !== id));
      toast.success('Loss calculation record deleted successfully');
    } catch (error) {
      toast.error('Failed to delete loss record');
      console.error('Error deleting loss record:', error);
    }
  };

  // Update status
  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const record = lossRecords.find(r => r.id === id);
      const updated = await lossCalAPI.update(id, { ...record, status: newStatus });
      setLossRecords(lossRecords.map(r => r.id === id ? updated : r));
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
      console.error('Error updating status:', error);
    }
  };

  // Filter records
  const filteredRecords = lossRecords.filter(record =>
    record.dept?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    record.remarks?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onProcessRecords = filteredRecords.filter(r => r.status === 'On Process');
  const completedRecords = filteredRecords.filter(r => r.status === 'Completed');

  // Export to Excel
  const exportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = filteredRecords.map(record => ({
        'Date': new Date(record.date).toLocaleDateString(),
        'Department': record.dept,
        'Weight (gm)': record.wt,
        'Act Purity (%)': record.actPurity,
        'Pure (gm)': record.pure,
        'Less (%)': record.less,
        'Rec (gm)': record.rec,
        'Before Melting Weight': record.pureBeforeWt || 0,
        'After Melting Weight': record.afterWt || 0,
        'Pure Loss (gm)': record.pureLoss,
        'Balance Pure': record.balancePure,
        'Remarks': record.remarks || '',
        'Status': record.status
      }));

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Loss Calculations");

      // Auto-size columns
      const colWidths = [
        { wch: 12 }, // Date
        { wch: 15 }, // Department
        { wch: 12 }, // Weight
        { wch: 12 }, // Act Purity
        { wch: 12 }, // Pure
        { wch: 10 }, // Less
        { wch: 12 }, // Rec
        { wch: 18 }, // Before Melting Weight
        { wch: 18 }, // After Melting Weight
        { wch: 15 }, // Pure Loss
        { wch: 15 }, // Balance Pure
        { wch: 20 }, // Remarks
        { wch: 12 }, // Status
      ];
      ws['!cols'] = colWidths;

      // Generate filename with current date
      const today = new Date().toISOString().split('T')[0];
      const filename = `Loss_Calculations_${today}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      toast.success(`Exported ${exportData.length} records to ${filename}`);
    } catch (error) {
      toast.error('Failed to export data to Excel');
      console.error('Export error:', error);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const variant = status === 'Completed' ? 'default' : 'secondary';
    const Icon = status === 'Completed' ? CheckCircle : Clock;
    
    return (
      <Badge variant={variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Calculator className="h-8 w-8 mx-auto mb-4 animate-spin" />
          <p>Loading loss calculations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="h-8 w-8 text-emerald-600" />
            Loss Calculator
          </h1>
          <p className="text-gray-600 mt-2">Track and calculate purity losses in jewelry manufacturing</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Export Button */}
          <Button variant="outline" onClick={exportToExcel} className="gap-2">
            <Download className="h-4 w-4" />
            Export to Excel
          </Button>
          
          {/* Add Record Dialog */}
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            {canAdd('loss-cal') && (
              <DialogTrigger asChild>
                <Button className="shadow-emerald">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Loss Record
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Loss Calculation Record</DialogTitle>
              <DialogDescription>Enter the details for the new loss calculation</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newRecord.date}
                    onChange={(e) => setNewRecord({ ...newRecord, date: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="dept">Department *</Label>
                  <Input
                    id="dept"
                    value={newRecord.dept}
                    onChange={(e) => setNewRecord({ ...newRecord, dept: e.target.value })}
                    placeholder="e.g., Casting, Polish"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="wt">Weight (gm) *</Label>
                  <Input
                    id="wt"
                    type="number"
                    step="0.001"
                    value={newRecord.wt}
                    onChange={(e) => setNewRecord({ ...newRecord, wt: e.target.value })}
                    placeholder="e.g., 10.500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="actPurity">Act Purity (%) *</Label>
                  <Input
                    id="actPurity"
                    type="number"
                    step="0.01"
                    value={newRecord.actPurity}
                    onChange={(e) => setNewRecord({ ...newRecord, actPurity: e.target.value })}
                    placeholder="e.g., 91.60"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="less">Less (%)</Label>
                  <Input
                    id="less"
                    type="number"
                    step="0.01"
                    value={newRecord.less}
                    onChange={(e) => setNewRecord({ ...newRecord, less: e.target.value })}
                    placeholder="e.g., 1.50"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="pureLoss">Pure Loss (gm) *</Label>
                  <Input
                    id="pureLoss"
                    type="number"
                    step="0.001"
                    value={newRecord.pureLoss}
                    onChange={(e) => setNewRecord({ ...newRecord, pureLoss: e.target.value })}
                    placeholder="e.g., 0.157"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="pureBeforeWt">Before Melting Weight (gm)</Label>
                  <Input
                    id="pureBeforeWt"
                    type="number"
                    step="0.001"
                    value={newRecord.pureBeforeWt}
                    onChange={(e) => setNewRecord({ ...newRecord, pureBeforeWt: e.target.value })}
                    placeholder="e.g., 9.500"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="afterWt">After Melting Weight (gm)</Label>
                  <Input
                    id="afterWt"
                    type="number"
                    step="0.001"
                    value={newRecord.afterWt}
                    onChange={(e) => setNewRecord({ ...newRecord, afterWt: e.target.value })}
                    placeholder="e.g., 9.200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={newRecord.status} onValueChange={(value) => setNewRecord({ ...newRecord, status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="On Process">On Process</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div></div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="remarks">Remarks</Label>
                <Textarea
                  id="remarks"
                  value={newRecord.remarks}
                  onChange={(e) => setNewRecord({ ...newRecord, remarks: e.target.value })}
                  placeholder="Additional notes or comments"
                  rows={2}
                />
              </div>

              {/* Auto-calculated Preview */}
              {(newRecord.wt && newRecord.actPurity && newRecord.pureLoss) && (
                <div className="border-t pt-4">
                  <Label className="text-sm font-semibold text-emerald-600">Auto-calculated Values (Excel Formulas):</Label>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    <div className="text-sm">
                      <span className="text-gray-600">Pure (E):</span>
                      <span className="ml-2 font-mono">{calculateFields(newRecord).pure} gm</span>
                      <p className="text-xs text-gray-500">WT × Act Purity%</p>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Rec (G):</span>
                      <span className="ml-2 font-mono">{calculateFields(newRecord).rec} gm</span>
                      <p className="text-xs text-gray-500">WT × (Act Purity - Less)%</p>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Balance Pure:</span>
                      <span className="ml-2 font-mono">{calculateFields(newRecord).balancePure} gm</span>
                      <p className="text-xs text-gray-500">Pure - Pure Loss</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddRecord}>Add Record</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by department or remarks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md"
            />
          </div>
        </CardContent>
      </Card>

      {/* Records organized by status */}
      <Tabs defaultValue="on-process" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="on-process" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            On Process ({onProcessRecords.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Completed ({completedRecords.length})
          </TabsTrigger>
        </TabsList>

        {/* On Process Records */}
        <TabsContent value="on-process">
          <Card>
            <CardHeader>
              <CardTitle>On Process Records</CardTitle>
              <CardDescription>Loss calculations currently being processed</CardDescription>
            </CardHeader>
            <CardContent>
              {onProcessRecords.length === 0 ? (
                <div className="text-center py-12">
                  <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No records on process</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table className="min-w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Dept</TableHead>
                          <TableHead>WT (gm)</TableHead>
                          <TableHead>Act Purity (%)</TableHead>
                          <TableHead>Pure (gm)</TableHead>
                          <TableHead>Less (%)</TableHead>
                          <TableHead>Rec (gm)</TableHead>
                          <TableHead>Before Melting Weight</TableHead>
                          <TableHead>After Melting Weight</TableHead>
                          <TableHead>Pure Loss (gm)</TableHead>
                          <TableHead>Balance Pure</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {onProcessRecords.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                            <TableCell className="font-medium">{record.dept}</TableCell>
                            <TableCell>{record.wt}</TableCell>
                            <TableCell>{record.actPurity}%</TableCell>
                            <TableCell className="font-mono text-blue-600">{record.pure}</TableCell>
                            <TableCell>{record.less}%</TableCell>
                            <TableCell className="font-mono text-green-600">{record.rec}</TableCell>
                            <TableCell>{record.pureBeforeWt || '-'}</TableCell>
                            <TableCell>{record.afterWt || '-'}</TableCell>
                            <TableCell className="font-mono text-red-600">{record.pureLoss}</TableCell>
                            <TableCell className="font-mono text-emerald-600">{record.balancePure}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <StatusBadge status={record.status} />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleStatusUpdate(record.id, 'Completed')}
                                >
                                  Mark Complete
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {canEdit('loss-cal') && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditDialog(record)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {canDelete('loss-cal') && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteRecord(record.id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Completed Records */}
        <TabsContent value="completed">
          <Card>
            <CardHeader>
              <CardTitle>Completed Records</CardTitle>
              <CardDescription>Finished loss calculations</CardDescription>
            </CardHeader>
            <CardContent>
              {completedRecords.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No completed records</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table className="min-w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Dept</TableHead>
                          <TableHead>WT (gm)</TableHead>
                          <TableHead>Act Purity (%)</TableHead>
                          <TableHead>Pure (gm)</TableHead>
                          <TableHead>Less (%)</TableHead>
                          <TableHead>Rec (gm)</TableHead>
                          <TableHead>Before Melting Weight</TableHead>
                          <TableHead>After Melting Weight</TableHead>
                          <TableHead>Pure Loss (gm)</TableHead>
                          <TableHead>Balance Pure</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {completedRecords.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                            <TableCell className="font-medium">{record.dept}</TableCell>
                            <TableCell>{record.wt}</TableCell>
                            <TableCell>{record.actPurity}%</TableCell>
                            <TableCell className="font-mono text-blue-600">{record.pure}</TableCell>
                            <TableCell>{record.less}%</TableCell>
                            <TableCell className="font-mono text-green-600">{record.rec}</TableCell>
                            <TableCell>{record.pureBeforeWt || '-'}</TableCell>
                            <TableCell>{record.afterWt || '-'}</TableCell>
                            <TableCell className="font-mono text-red-600">{record.pureLoss}</TableCell>
                            <TableCell className="font-mono text-emerald-600">{record.balancePure}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <StatusBadge status={record.status} />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleStatusUpdate(record.id, 'On Process')}
                                >
                                  Reprocess
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {canEdit('loss-cal') && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditDialog(record)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {canDelete('loss-cal') && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteRecord(record.id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Loss Calculation Record</DialogTitle>
            <DialogDescription>Update the loss calculation details</DialogDescription>
          </DialogHeader>
          {editingRecord && (
            <div className="grid gap-4 py-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={editingRecord.date}
                    onChange={(e) => setEditingRecord({ ...editingRecord, date: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Department *</Label>
                  <Input
                    value={editingRecord.dept}
                    onChange={(e) => setEditingRecord({ ...editingRecord, dept: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Weight (gm) *</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={editingRecord.wt}
                    onChange={(e) => setEditingRecord({ ...editingRecord, wt: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label>Act Purity (%) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingRecord.actPurity}
                    onChange={(e) => setEditingRecord({ ...editingRecord, actPurity: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Less (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingRecord.less || ''}
                    onChange={(e) => setEditingRecord({ ...editingRecord, less: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Pure Loss (gm) *</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={editingRecord.pureLoss || ''}
                    onChange={(e) => setEditingRecord({ ...editingRecord, pureLoss: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Before Melting Weight (gm)</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={editingRecord.pureBeforeWt || ''}
                    onChange={(e) => setEditingRecord({ ...editingRecord, pureBeforeWt: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>After Melting Weight (gm)</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={editingRecord.afterWt || ''}
                    onChange={(e) => setEditingRecord({ ...editingRecord, afterWt: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Status</Label>
                  <Select value={editingRecord.status} onValueChange={(value) => setEditingRecord({ ...editingRecord, status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="On Process">On Process</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div></div>
              </div>

              <div className="grid gap-2">
                <Label>Remarks</Label>
                <Textarea
                  value={editingRecord.remarks || ''}
                  onChange={(e) => setEditingRecord({ ...editingRecord, remarks: e.target.value })}
                  rows={2}
                />
              </div>

              {/* Auto-calculated Preview */}
              {(editingRecord.wt && editingRecord.actPurity && editingRecord.pureLoss) && (
                <div className="border-t pt-4">
                  <Label className="text-sm font-semibold text-emerald-600">Updated Auto-calculated Values (Excel Formulas):</Label>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    <div className="text-sm">
                      <span className="text-gray-600">Pure (E):</span>
                      <span className="ml-2 font-mono">{calculateFields(editingRecord).pure} gm</span>
                      <p className="text-xs text-gray-500">WT × Act Purity%</p>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Rec (G):</span>
                      <span className="ml-2 font-mono">{calculateFields(editingRecord).rec} gm</span>
                      <p className="text-xs text-gray-500">WT × (Act Purity - Less)%</p>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Balance Pure:</span>
                      <span className="ml-2 font-mono">{calculateFields(editingRecord).balancePure} gm</span>
                      <p className="text-xs text-gray-500">Pure - Pure Loss</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditRecord}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LossCal;